import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Dropdown } from '../dropdown/DropDown.tsx';
import { Global01NavNavmoreLineIcon, Icon } from '@kim/ui';
import classNames from 'classnames';
import Tooltips from 'rc-tooltip';
import intl from '@/internationalzation/intl';
import styles from './index.module.less';

type ISessionItemProps = {
  onClick?: (id: string) => void;
  onShare?: (id: string) => void;
  onRename?: (id: string, name: string) => void;
  onDelete?: (id: string) => void;
  active?: boolean;
  title?: string;
  highlightText?: string;
  id: string;
  editable?: boolean;
  icon?: string;
  status?: string;
  hasUnread?: boolean;
  matchedAgentName?: string;
};

export function isRunning(status: string) {
  return ['running', 'pending'].includes(status || '');
}

export const SessionItem = React.memo((props: ISessionItemProps) => {
  const {
    id,
    title = '',
    onClick,
    active = false,
    highlightText = '',
    editable = false,
    status = '',
    icon = '',
    onRename,
    onDelete,
    onShare,
    hasUnread = false,
    matchedAgentName,
  } = props;

  const infoRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState(false);
  const realTitle = useMemo(() => {
    return getHighlightText(title, highlightText);
  }, [title, highlightText]);

  const [showTooltip, setShowTooltip] = useState(false);

  const handleRename = useCallback(() => {
    editable && onRename?.(id, title);
  }, [editable, id, title, onRename]);

  const dropList = useMemo(() => {
    const items: Array<{
      id: string;
      iconNode: React.ReactNode;
      text: string;
      command: () => void;
    }> = [];

    if (onShare) {
      items.push({
        id: 'share',
        iconNode: (
          <Icon name="eed_web_icon_global_03_action_zhuanfa_line" size={16} />
        ),
        text: intl.t('mario.button.share'),
        command: () => {
          onShare(id);
        },
      });
    }

    items.push(
      {
        id: 'edit',
        iconNode: (
          <Icon
            name="eed_web_icon_global_04_textaction_bianji_line"
            size={16}
          />
        ),
        text: intl.t('mario.button.edit'),
        command: () => {
          handleRename();
        },
      },
      {
        id: 'delete',
        iconNode: (
          <Icon
            name="eed_web_icon_global_04_textaction_shanchu_line"
            size={16}
          />
        ),
        text: intl.t('mario.button.delete'),
        command: () => {
          onDelete?.(id);
        },
      },
    );

    return items;
  }, [id, onDelete, onShare, handleRename]);

  useEffect(() => {
    if (!infoRef.current) return;
    const { width } = infoRef.current.getBoundingClientRect();
    const tempTitle = document.createElement('div');
    tempTitle.className = styles.infoTitle;
    tempTitle.innerHTML = title;
    tempTitle.style.position = 'absolute';
    tempTitle.style.zIndex = '0';
    infoRef.current.appendChild(tempTitle);
    const { width: tempWidth } = tempTitle.getBoundingClientRect();
    infoRef.current.removeChild(tempTitle);
    setShowTooltip(tempWidth > width);
  }, [title]);

  return (
    <div
      className={classNames(styles.sessionItem, { [styles.active]: active })}
      onClick={() => {
        onClick?.(id);
      }}
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
      onDoubleClick={handleRename}
    >
      {icon && (
        <div className={styles.iconWrapper}>
          {isRunning(status) && <div className={styles.spinnerSvg} />}
          <div className={styles.iconWrapperInner}>
            <img src={icon} className={styles.icon} />
          </div>
        </div>
      )}

      <div className={styles.info} ref={infoRef}>
        {showTooltip ? (
          <Tooltips
            overlayClassName={styles.infoTitleTooltip}
            overlay={realTitle}
            placement="top"
            mouseEnterDelay={0.2}
            overlayStyle={{ maxWidth: 248 }}
          >
            <div className={styles.infoTitle}>{realTitle}</div>
          </Tooltips>
        ) : (
          <div className={styles.infoTitle}>{realTitle}</div>
        )}
      </div>

      {matchedAgentName ? (
        <div className={styles.agentTag}>
          <span className={styles.agentTagText}>{matchedAgentName}</span>
        </div>
      ) : null}

      {editable && hover && (
        <div
          className={styles.editIcon}
          onClick={(e) => e.stopPropagation()}
        >
          <Dropdown
            dropList={dropList}
            placement="bottom-start"
            trigger="click"
            offset={[0, 14]}
            className={styles.sessionDropdown}
            onVisibleChange={(visible) => {
              if (!visible) {
                setHover(false);
              }
            }}
          >
            <Global01NavNavmoreLineIcon size={14} className="edit-icon" />
          </Dropdown>
        </div>
      )}

      {!active && hasUnread && <span className={styles.unreadDot} />}
    </div>
  );
});

function getHighlightText(text: string, keyword: string) {
  if (!keyword || !text) return text;

  const regex = new RegExp(
    `(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
    'gi',
  );
  const parts = text.split(regex);

  return parts.map((part, index) =>
    regex.test(part) ? (
      <span key={index} className={styles.highlightText}>
        {part}
      </span>
    ) : (
      part
    ),
  );
}
